import React, { useState, useCallback } from 'react';

import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faTrash, faCloudUploadAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Papa from 'papaparse';
import Swal from 'sweetalert2';

import { backgroundGrey1, bootstrapSecondary, grape, lightGrayBorder } from 'lib/css/colors';
import EntityType from 'lib/enums/EntityType';
import {
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  GLOBAL_VENDORS_FOR_DROPDOWN,
  INTERNAL_TOOLS_DELETE_VENDOR_PRODUCTS,
  INTERNAL_TOOLS_DELETE_MANUFACTURER_PRODUCTS,
  INTERNAL_TOOLS_PRODUCT_STATS,
  getURL,
} from 'lib/networking/endpoints';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

// Constants and Enums
const ENTITY_TYPES = {
  DISTRIBUTOR: EntityType.DISTRIBUTOR,
  MANUFACTURER: EntityType.MANUFACTURER,
};

const ALERT_VARIANTS = {
  SUCCESS: 'success',
  WARNING: 'warning',
  DANGER: 'danger',
  INFO: 'info',
};

const MESSAGES = {
  SELECT_ENTITY: 'Please select an entity',
  CONFIRM_DELETION: 'Confirm Deletion',
  SUCCESS_TITLE: 'Success!',
  WARNING_TITLE: 'Warning!',
  ERROR_TITLE: 'Error!',
};

function DeleteEntityProducts() {
  const [entityType, setEntityType] = useState(ENTITY_TYPES.DISTRIBUTOR);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [upbProductsOnly, setUpbProductsOnly] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: ALERT_VARIANTS.INFO });

  // Product stats state
  const [statsParams, setStatsParams] = useState(null);
  const [productStats, setProductStats] = useState(null);

  // CSV file upload state
  const [csvFilename, setCsvFilename] = useState('');
  const [skuList, setSkuList] = useState([]);
  const [csvError, setCsvError] = useState('');
  const [useSkuFilter, setUseSkuFilter] = useState(false);
  const fileInputRef = React.useRef(null);

  const showAlert = (message, variant = ALERT_VARIANTS.INFO) => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: ALERT_VARIANTS.INFO }), 3000);
  };

  // Hook for fetching product stats
  const {
    data: statsData,
    loading: statsLoading,
    refetch: fetchStats,
  } = useGet(
    statsParams
      ? getURL(INTERNAL_TOOLS_PRODUCT_STATS, {
          entity_type: statsParams.entity_type,
          entity_id: statsParams.entity_id,
        })
      : '',
    {},
    !statsParams, // skip if no params
    true, // lazy load
  );

  // Auto-fetch when statsParams changes
  React.useEffect(() => {
    if (statsParams) {
      fetchStats();
    }
  }, [statsParams, fetchStats]);

  // Handle stats data when it arrives
  React.useEffect(() => {
    if (statsData?.data) {
      setProductStats(statsData.data);
      showAlert('Product statistics loaded successfully', ALERT_VARIANTS.SUCCESS);
    }
  }, [statsData]);

  // Auto-fetch stats when entity is selected
  React.useEffect(() => {
    if (selectedEntity) {
      setStatsParams({
        entity_type: entityType,
        entity_id: selectedEntity.value,
      });
    }
  }, [selectedEntity, entityType]);

  // Handle CSV file upload
  const handleCSVUpload = event => {
    const file = event.target.files[0];
    if (!file) return;

    setCsvFilename(file.name);
    setCsvError('');
    setUseSkuFilter(false);

    const reader = new FileReader();
    reader.onload = e => {
      const csvData = e.target.result;
      Papa.parse(csvData, {
        header: true,
        skipEmptyLines: true,
        complete: results => {
          // Look for a column named "SKUs" or similar
          const headers = results.meta.fields;
          const skuColumn = headers.find(
            header => header.toLowerCase() === 'skus' || header.toLowerCase() === 'sku',
          );

          if (skuColumn) {
            // Extract unique SKUs
            const skus = results.data
              .map(row => row[skuColumn])
              .filter(sku => sku && sku.trim() !== '');

            setSkuList(skus);
            setUseSkuFilter(true);
            Swal.fire({
              icon: 'success',
              title: 'SKUs Loaded',
              text: `Successfully loaded ${skus.length} SKUs from the CSV file.`,
            });
          } else {
            setCsvError('No column named "SKUs" or "SKU" found in the CSV file.');
          }
        },
        error: error => {
          setCsvError(`Error parsing CSV: ${error.message}`);
        },
      });
    };
    reader.onerror = () => {
      setCsvError('Error reading file');
    };
    reader.readAsText(file);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const resetFileUpload = () => {
    setCsvFilename('');
    setSkuList([]);
    setUseSkuFilter(false);
    setCsvError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // usePost for deleting distributor products
  const { postData: deleteVendorProductsPost, loading: deletingVendor } = usePost(
    INTERNAL_TOOLS_DELETE_VENDOR_PRODUCTS,
    res => {
      const result = res?.data?.data;
      if (result) {
        Swal.fire({
          title: 'Task Queued',
          html: `
            <p>${result.message}</p>
            <p>The deletion task has been queued and will be processed shortly.
            You can monitor the progress in the Tasks page.</p>
          `,
          icon: ALERT_VARIANTS.SUCCESS,
          confirmButtonText: 'OK',
        });
        setProductStats(null);
        setStatsParams(null);
      }
    },
    error => {
      const errorMsg = error?.response?.data?.error || 'Failed to queue deletion task';
      Swal.fire({
        title: MESSAGES.ERROR_TITLE,
        text: errorMsg,
        icon: 'error',
        confirmButtonText: 'OK',
      });
    },
  );

  // usePost for deleting manufacturer products
  const { postData: deleteManufacturerProductsPost, loading: deletingManufacturer } = usePost(
    INTERNAL_TOOLS_DELETE_MANUFACTURER_PRODUCTS,
    res => {
      const result = res?.data?.data;
      if (result) {
        Swal.fire({
          title: 'Task Queued',
          html: `
            <p>${result.message}</p>
            <p>The deletion task has been queued and will be processed shortly.
            You can monitor the progress in the Tasks page.</p>
          `,
          icon: ALERT_VARIANTS.SUCCESS,
          confirmButtonText: 'OK',
        });
        setProductStats(null);
        setStatsParams(null);
      }
    },
    error => {
      const errorMsg = error?.response?.data?.error || 'Failed to queue deletion task';
      Swal.fire({
        title: MESSAGES.ERROR_TITLE,
        text: errorMsg,
        icon: 'error',
        confirmButtonText: 'OK',
      });
    },
  );

  const handleDeleteProducts = useCallback(() => {
    if (!selectedEntity) {
      Swal.fire({
        title: MESSAGES.WARNING_TITLE,
        text: MESSAGES.SELECT_ENTITY,
        icon: 'warning',
        confirmButtonText: 'OK',
      });
      return;
    }

    // Build confirmation message
    let confirmMessage = `Are you sure you want to queue a deletion task for `;

    if (upbProductsOnly && entityType === ENTITY_TYPES.MANUFACTURER) {
      confirmMessage += `UPB products`;
    } else if (useSkuFilter && skuList.length > 0) {
      confirmMessage += `${skuList.length} specific SKUs`;
    } else {
      confirmMessage += `all products`;
    }

    confirmMessage +=
      ` for ${selectedEntity.label}? ` +
      `The deletion will be processed asynchronously and may take several minutes. ` +
      `This action cannot be undone.`;

    // Show confirmation dialog
    Swal.fire({
      title: MESSAGES.CONFIRM_DELETION,
      text: confirmMessage,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, queue deletion',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (result.isConfirmed) {
        // Build request body with appropriate parameters
        const requestBody = {
          entity_id: selectedEntity.value,
          entity_name: selectedEntity.label,
        };

        // Add SKU filter if enabled
        if (useSkuFilter && skuList.length > 0) {
          requestBody.sku_list = skuList;
        }

        // Add UPB filter if enabled (only for manufacturers)
        if (upbProductsOnly && entityType === ENTITY_TYPES.MANUFACTURER) {
          requestBody.upb_products_only = true;
        }

        // Call appropriate endpoint based on entity type
        if (entityType === ENTITY_TYPES.DISTRIBUTOR) {
          deleteVendorProductsPost(requestBody);
        } else {
          deleteManufacturerProductsPost(requestBody);
        }
      }
    });
  }, [
    entityType,
    selectedEntity,
    upbProductsOnly,
    useSkuFilter,
    skuList,
    deleteVendorProductsPost,
    deleteManufacturerProductsPost,
  ]);

  const handleEntityTypeChange = entityTypeValue => {
    setEntityType(entityTypeValue);
    setSelectedEntity(null);
    setUpbProductsOnly(false);
    setProductStats(null);
    setStatsParams(null);
    // Reset CSV upload
    resetFileUpload();
  };

  const deleting = deletingVendor || deletingManufacturer;

  return (
    <>
      {alert.show && (
        <Alert
          variant={alert.variant}
          onClose={() => setAlert({ ...alert, show: false })}
          dismissible
        >
          {alert.message}
        </Alert>
      )}

      <Card className={css(styles.card)}>
        <Card.Body>
          <h2 className={css(styles.sectionTitle)}>Delete Entity Products</h2>
          <hr />

          <Form>
            <Row>
              {/* Form Fields */}
              <Col md={selectedEntity ? 7 : 12}>
                <Form.Group className={css(styles.formGroup)}>
                  <Form.Label>Entity Type</Form.Label>
                  <div>
                    <Form.Check
                      inline
                      type="radio"
                      label="Distributor"
                      value={ENTITY_TYPES.DISTRIBUTOR}
                      checked={entityType === ENTITY_TYPES.DISTRIBUTOR}
                      onChange={e => handleEntityTypeChange(e.target.value)}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      label="Manufacturer"
                      value={ENTITY_TYPES.MANUFACTURER}
                      checked={entityType === ENTITY_TYPES.MANUFACTURER}
                      onChange={e => handleEntityTypeChange(e.target.value)}
                    />
                  </div>
                </Form.Group>

                <Form.Group className={css(styles.formGroup)}>
                  <Form.Label>
                    Select{' '}
                    {entityType === ENTITY_TYPES.DISTRIBUTOR ? 'Distributor' : 'Manufacturer'}
                  </Form.Label>
                  {entityType === ENTITY_TYPES.DISTRIBUTOR ? (
                    <SearchableScrollableSelect
                      key="distributor-select"
                      initOption={selectedEntity}
                      onChange={option => {
                        setSelectedEntity(option);
                      }}
                      fetchUrl={GLOBAL_VENDORS_FOR_DROPDOWN}
                      optionMapper={vendor => ({
                        label: vendor.name,
                        value: vendor.id,
                      })}
                      autoFocus={false}
                      placeholder="Search and select distributor..."
                    />
                  ) : (
                    <SearchableScrollableSelect
                      key="manufacturer-select"
                      initOption={selectedEntity}
                      onChange={option => {
                        setSelectedEntity(option);
                      }}
                      fetchUrl={GLOBAL_MANUFACTURERS_FOR_DROPDOWN}
                      optionMapper={manufacturer => ({
                        label: manufacturer.name,
                        value: manufacturer.id,
                      })}
                      autoFocus={false}
                      placeholder="Search and select manufacturer..."
                    />
                  )}
                </Form.Group>

                {/* UPB Products Option (only for manufacturers) */}
                {entityType === ENTITY_TYPES.MANUFACTURER && (
                  <Form.Group className={css(styles.formGroup)}>
                    <Form.Check
                      type="checkbox"
                      label="Delete UPB Products Only"
                      checked={upbProductsOnly}
                      onChange={e => {
                        setUpbProductsOnly(e.target.checked);
                        // If UPB option is checked, disable SKU filter
                        if (e.target.checked) {
                          resetFileUpload();
                        }
                      }}
                    />
                  </Form.Group>
                )}

                {/* CSV Upload for SKU Filtering (disabled if UPB is selected) */}
                {!upbProductsOnly && (
                  <Form.Group className={css(styles.formGroup)}>
                    <h5>Filter by SKUs</h5>
                    <p className={css(styles.subText)}>
                      Upload a CSV file with a column named SKUs to filter the deletion to specific
                      products
                    </p>

                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleCSVUpload}
                      accept=".csv"
                      style={{ display: 'none' }}
                    />

                    <div className={css(styles.dropzoneBox)} onClick={triggerFileInput}>
                      <div>
                        <div>
                          <FontAwesomeIcon icon={faCloudUploadAlt} />
                        </div>
                        <span>{csvFilename || 'Drop a CSV file here or click to upload'}</span>
                      </div>
                    </div>

                    {csvFilename && (
                      <Button variant="link" onClick={resetFileUpload} className="mt-2 p-0">
                        Remove file
                      </Button>
                    )}

                    {csvError && (
                      <Alert variant="danger" className="mt-2">
                        {csvError}
                      </Alert>
                    )}

                    {useSkuFilter && skuList.length > 0 && (
                      <Alert variant="success" className="mt-2">
                        {`${skuList.length} SKUs loaded from file.` +
                          ` Deletion will be filtered to only these SKUs.`}
                      </Alert>
                    )}
                  </Form.Group>
                )}
              </Col>

              {/* Product Statistics Display */}
              {selectedEntity && (
                <Col md={5}>
                  <Card className={css(styles.statsCard)}>
                    <Card.Body>
                      <h5 className={css(styles.statsTitle)}>Product Statistics</h5>
                      {statsLoading ? (
                        <div className={css(styles.statsLoading)}>
                          <Spinner animation="border" size="sm" />
                          <span className="ms-2">Loading statistics...</span>
                        </div>
                      ) : productStats ? (
                        <div className={css(styles.statsContent)}>
                          <div className={css(styles.statRow)}>
                            <span className={css(styles.statLabel)}>Entity:</span>
                            <span className={css(styles.statValue)}>
                              {productStats.entity_name}
                            </span>
                          </div>
                          <div className={css(styles.statRow)}>
                            <span className={css(styles.statLabel)}>Total Products:</span>
                            <span className={css(styles.statValue, styles.statHighlight)}>
                              {productStats.total_products?.toLocaleString() || 0}
                            </span>
                          </div>
                          {entityType === ENTITY_TYPES.MANUFACTURER &&
                            productStats.upb_products !== undefined && (
                              <div className={css(styles.statRow)}>
                                <span className={css(styles.statLabel)}>UPB Products:</span>
                                <span className={css(styles.statValue)}>
                                  {productStats.upb_products?.toLocaleString() || 0}
                                </span>
                              </div>
                            )}
                        </div>
                      ) : (
                        <div className={css(styles.statsError)}>
                          <p>Failed to load statistics</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              )}
            </Row>

            {/* Delete Button */}
            <div className={css(styles.deleteButtonContainer)}>
              <Button
                variant="danger"
                size="lg"
                onClick={handleDeleteProducts}
                disabled={!selectedEntity || deleting}
                className={css(styles.deleteButton)}
              >
                {deleting ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" /> Deleting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrash} /> Delete Products
                  </>
                )}
              </Button>
            </div>

            <Alert variant="warning" className={css(styles.warningBox)}>
              <strong>Warning:</strong>{' '}
              {upbProductsOnly && entityType === ENTITY_TYPES.MANUFACTURER
                ? `This action will delete all UPB products for the selected manufacturer.`
                : useSkuFilter && skuList.length > 0
                ? `This action will delete ${skuList.length} specific products ` +
                  `for the selected ${entityType}.`
                : `This action will delete all products for the selected ${entityType}.`}{' '}
              The deletion will be processed asynchronously and cannot be undone. Please ensure you
              have selected the correct {useSkuFilter ? 'entity and SKU list' : 'entity'}.
            </Alert>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
}

const styles = StyleSheet.create({
  card: {
    marginTop: '1.5rem',
    padding: '1.5rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  deleteButtonContainer: {
    textAlign: 'center',
    marginTop: '2rem',
    marginBottom: '1.5rem',
  },
  deleteButton: {
    minWidth: '200px',
    fontWeight: '600',
  },
  warningBox: {
    marginTop: '1rem',
  },
  subText: {
    fontSize: '0.9rem',
    color: bootstrapSecondary,
    marginBottom: '0.75rem',
  },
  dropzoneBox: {
    border: `2px dashed ${lightGrayBorder}`,
    borderRadius: '8px',
    padding: '2rem',
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: backgroundGrey1,
    transition: 'all 0.2s ease',
    ':hover': {
      borderColor: grape,
      backgroundColor: '#f8f7fc',
    },
  },
  statsCard: {
    marginTop: '0',
    height: 'fit-content',
    position: 'sticky',
    top: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    border: `1px solid ${lightGrayBorder}`,
  },
  statsTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
    color: grape,
    borderBottom: `2px solid ${grape}`,
    paddingBottom: '0.5rem',
  },
  statsContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0',
    borderBottom: `1px solid ${lightGrayBorder}`,
    ':last-child': {
      borderBottom: 'none',
    },
  },
  statLabel: {
    fontSize: '0.9rem',
    color: bootstrapSecondary,
    fontWeight: '500',
  },
  statValue: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#333',
  },
  statHighlight: {
    color: grape,
    fontSize: '1.1rem',
  },
  statsLoading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    color: bootstrapSecondary,
  },
  statsError: {
    textAlign: 'center',
    padding: '2rem',
    color: bootstrapSecondary,
  },
});

export default DeleteEntityProducts;
